import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Music, Lightbulb } from "lucide-react";
import { toast } from "sonner";

function ChipList({ items, testid }) {
  if (!items?.length) return null;
  return (
    <ul className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
      {items.map((it, i) => (
        <li
          key={`${it}-${i}`}
          className="font-mono text-xs px-2.5 py-1 rounded-full border border-accent/40 text-accent bg-accent/5 uppercase tracking-widest"
        >
          {it}
        </li>
      ))}
    </ul>
  );
}

export default function AiAnalysisPanel({ song, onChange }) {
  const [loading, setLoading] = useState(false);
  const analysis = song?.ai_analysis;
  const hasAnalysis = analysis && typeof analysis === "object" && !analysis.raw;

  const analyze = async () => {
    setLoading(true);
    try {
      await api.post("/ai/analyze", { song_id: song.id });
      toast.success("Song analyzed.");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <section
      data-testid="ai-analysis-panel"
      className="grain-panel border border-accent/30 rounded-2xl p-6 bg-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-accent/50 text-accent shrink-0 glow-amber-sm">
            <Sparkles size={18} />
          </span>
          <div>
            <p className="font-medium">Memory map</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {hasAnalysis
                ? "Structural anchors and rhyme pairs to help this song stick."
                : "Let the coach mark rhymes, structure, and the story arc so your brain has hooks to hang on."}
            </p>
          </div>
        </div>
        <Button
          onClick={analyze}
          disabled={loading}
          variant={hasAnalysis ? "outline" : "default"}
          className="rounded-full h-10 px-5"
          data-testid="ai-analysis-run-btn"
        >
          <Sparkles size={14} className="mr-1.5" />
          {loading ? "Analyzing…" : hasAnalysis ? "Re-analyze" : "Analyze this song"}
        </Button>
      </div>

      {hasAnalysis && (
        <div className="mt-6 space-y-6">
          {analysis.story && (
            <div data-testid="ai-analysis-story">
              <p className="overline-label text-accent">The story</p>
              <p className="mt-2 lyric-line text-lg leading-relaxed">{analysis.story}</p>
            </div>
          )}

          {Array.isArray(analysis.structure) && analysis.structure.length > 0 && (
            <div data-testid="ai-analysis-structure">
              <p className="overline-label text-accent">Structure</p>
              <ul className="flex flex-wrap items-center gap-2 mt-3">
                {analysis.structure.map((s, i) => (
                  <li key={`${s}-${i}`} className="flex items-center gap-2">
                    <span className="font-mono text-xs px-3 py-1 rounded-full border border-border bg-secondary text-foreground uppercase tracking-widest">
                      {s}
                    </span>
                    {i < analysis.structure.length - 1 && (
                      <ArrowRight size={12} className="text-muted-foreground" />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(analysis.anchors) && analysis.anchors.length > 0 && (
            <div data-testid="ai-analysis-anchors">
              <p className="overline-label text-accent">Memory anchors</p>
              <ul className="mt-3 grid sm:grid-cols-2 gap-3">
                {analysis.anchors.map((a, i) => (
                  <li key={`${a.section || "sec"}-${i}`} className="border border-border rounded-xl p-3 bg-secondary/40 flex items-start gap-2.5">
                    <Lightbulb size={14} className="mt-0.5 text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="overline-label text-muted-foreground">{a.section || "Section"}</p>
                      <p className="font-display text-lg mt-0.5">{a.cue || a.text || String(a)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(analysis.rhymes) && analysis.rhymes.length > 0 && (
            <div data-testid="ai-analysis-rhymes">
              <p className="overline-label text-accent">Rhyme pairs</p>
              <ul className="mt-3 flex flex-wrap gap-3">
                {analysis.rhymes.map((pair, i) => {
                  const key = Array.isArray(pair) ? `${pair.join("-")}-${i}` : `${String(pair)}-${i}`;
                  return (
                    <li
                      key={key}
                      className="flex items-center gap-1.5 font-display text-lg italic px-3 py-1.5 border border-primary/40 rounded-full text-primary bg-primary/5"
                    >
                      <Music size={12} />
                      <span>{Array.isArray(pair) ? pair.join(" · ") : String(pair)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {Array.isArray(analysis.tricky) && analysis.tricky.length > 0 && (
            <div data-testid="ai-analysis-tricky">
              <p className="overline-label text-accent">Tricky words to watch</p>
              <ChipList items={analysis.tricky} testid="ai-analysis-tricky-chips" />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
